import {
  ApplicationConfig,
  importProvidersFrom,
  isDevMode,
  LOCALE_ID
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideHttpClient,
  withJsonpSupport,
  withInterceptors
} from '@angular/common/http';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import {
  MAT_LEGACY_FORM_FIELD_DEFAULT_OPTIONS as MAT_FORM_FIELD_DEFAULT_OPTIONS
} from '@angular/material/legacy-form-field';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { NgxStripeModule } from 'ngx-stripe';
import { TimeagoModule } from 'ngx-timeago';
import { MarkdownModule } from 'ngx-markdown';
import { githubAlertsExtension } from '@zerops/zui/markdown/marked-extensions/github-alerts.extension';
import { headingIdsExtension } from '@zerops/zui/markdown/marked-extensions/heading-ids.extension';
import { externalLinksExtension } from '@zerops/zui/markdown/marked-extensions/external-links.extension';
import { codeCopyExtension } from '@zerops/zui/markdown/marked-extensions/code-copy.extension';
import { docsLinksExtension } from '@zerops/zui/markdown/marked-extensions/docs-links.extension';
import { translocoConfig, provideTransloco } from '@ngneat/transloco';
import { provideTranslocoLocale } from '@ngneat/transloco-locale';
import { provideTranslocoMessageformat } from '@ngneat/transloco-messageformat';
import { PROJECTINFO_ENDPOINT } from '@zerops/zui/project-info';
import { CODE_FIELD_SCHEMA_BASE_URL } from '@zerops/zui/code-field';
import { ZuiRecipesModule } from '@zerops/zui/recipes';
// import { ZefIntercomModule } from '@zerops/zef/intercom';
import { ZefNoolaModule } from '@zerops/zef/noola';
import { environment } from '@zerops/zerops/env';
import { appRoutes } from './app.routes';
import { appReducers, metaReducers } from './app.reducer';
import { PENDING_DEPLOY_CONFIG_FEATURE_NAME, pendingDeployConfigReducer } from './pending-deploy-config.state';
import { AppEffect } from './app.effect';
import { ClientUserRemovalEffect } from './client-user-removal.effect';
import { CoreModule } from '@zerops/zerops/core';
import { ZefLibModule } from '../core/zef-lib';
import { apiBaseInterceptor, apiErrorMessageInterceptor } from '../interceptors';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDateFnsDatetimeModule } from '@zerops/zef/datetimepicker';
import {
  MAT_LEGACY_TOOLTIP_DEFAULT_OPTIONS as MAT_TOOLTIP_DEFAULT_OPTIONS
} from '@angular/material/legacy-tooltip';
import { TerminalModule } from '../feature/terminal';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top'
      })
    ),
    provideHttpClient(
      withInterceptors([apiBaseInterceptor, apiErrorMessageInterceptor]),
      withJsonpSupport()
    ),
    importProvidersFrom(
      NgxStripeModule.forRoot(environment.stripePublishableKey),
      StoreModule.forRoot(appReducers, { metaReducers }),
      StoreModule.forFeature(PENDING_DEPLOY_CONFIG_FEATURE_NAME, pendingDeployConfigReducer),
      EffectsModule.forRoot([ AppEffect, ClientUserRemovalEffect ]),
      TimeagoModule.forRoot(),
      MarkdownModule.forRoot({
        markedExtensions: [
          githubAlertsExtension(),
          headingIdsExtension(),
          externalLinksExtension(), // Loaded first, but runs as fallback (returns false for docs links)
          docsLinksExtension(), // Loaded last = highest priority, handles docs.zerops.io links
          codeCopyExtension()
        ]
      }),
      // Intercom retired — Noola is the primary support chat. Re-enabling means
      // restoring this registration AND the injections/boot in app.container.ts,
      // app.effect.ts and app-bar.container.ts: `Intercom` is providedIn 'root'
      // but injects `IntercomConfig`, which only this forRoot() provides, so a
      // half-restore throws NullInjectorError at startup.
      // ZefIntercomModule.forRoot({
      //   updateOnRouterChange: true,
      //   app_id: environment.intercomAppId
      // }),
      ZefNoolaModule.forRoot({
        widget_key: environment.noolaKey,
        api_url: 'https://api.noola.cc',
        edge_url: 'wss://edge.noola.cc',
        hidden: true
      }),
      ZuiRecipesModule.forRoot(environment.webApiUrl),
      CoreModule,
      ZefLibModule,
      MatDateFnsDatetimeModule,
      TerminalModule.forRoot({
        forceSecuredEndpoint: environment.forceSecuredWebsocket
      })
    ),
    provideTransloco({
      config: translocoConfig({
        availableLangs: ['en'],
        defaultLang: 'en',
        prodMode: !isDevMode()
      })
    }),
    provideTranslocoMessageformat({
      locales: 'en-US'
    }),
    provideTranslocoLocale({
      langToLocaleMapping: {
        en: 'en-US'
      }
    }),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        floatLabel: 'always'
      }
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        disableTooltipInteractivity: true
      }
    },
    {
        provide: LOCALE_ID,
        useValue: 'cs'
    },
    {
      provide: MAT_DATE_LOCALE,
      useExisting: LOCALE_ID,
    },
    {
      provide: PROJECTINFO_ENDPOINT,
      useValue: environment.projectInfoUrl
    },
    {
      provide: CODE_FIELD_SCHEMA_BASE_URL,
      useValue: environment.apiUrl
    }
  ]
};
